import { style } from '@react-spectrum/s2/style' with { type: 'macro' };

import { BlobImage } from '@/components/blob/BlobImage';
import { InstagramLogo } from '@/components/icons/socials-monochrome/InstagramLogo';
import type { EffectiveStatus } from '@/hooks/useContentArtifacts';
import { useIsGeneratingImages } from '@/hooks/useIsGeneratingImages';
import {
	isScheduleObject,
	postScheduleIso,
	schedulePublishState,
} from '@/spaces/chat/panels/scheduleUtils';
import { cn } from '@/utils/cn';

import type { InstagramPost } from '../../../../bridge/types/artifact.ts';
import type { ProcessStatus } from '../../../../bridge/types/process.ts';

import { validMoment } from './emailSendDate';
import { TileFooter } from './TileFooter';
import { TileSelectionCheckbox } from './TileSelectionCheckbox';

interface PostAssetProps {
	post: InstagramPost;
	effectiveStatus?: EffectiveStatus;
	processStatus?: ProcessStatus;
	onClick?: () => void;
	isSelectable?: boolean;
	isSelected?: boolean;
	onToggleSelect?: () => void;
}

export const PostAsset = ({
	post,
	effectiveStatus,
	processStatus,
	onClick,
	isSelectable,
	isSelected,
	onToggleSelect,
}: PostAssetProps) => {
	const scheduledDate =
		isScheduleObject(post.schedule) && 'on' in post.schedule
			? post.schedule.on
			: null;
	const slides = post.content.image_urls ?? [];
	const status: EffectiveStatus = effectiveStatus ?? post.status;
	// The full instant, for every schedule kind. A legacy `fire_at` schedule has no
	// `on` key, so `scheduledDate` above is null for it and the tile showed no date
	// at all — the same blind spot that hid the past-due state.
	const scheduleInstant = postScheduleIso(post);
	// Both stored fields are unvalidated, and moment prints the literal "Invalid
	// date" for a value it cannot read, so only a readable one reaches the tile.
	const dateMoment = validMoment(scheduledDate) ?? validMoment(scheduleInstant);
	const isGenerating = useIsGeneratingImages(post, processStatus);
	// `scheduledDate` above is date-only, so it cannot say whether the minute has
	// passed. `scheduleInstant` carries the full instant and reads every schedule
	// kind the backend stores.
	const publishState =
		post.status === 'scheduled'
			? schedulePublishState(scheduleInstant, post.express_metadata)
			: 'on_time';

	return (
		<div
			className="group flex cursor-pointer flex-col gap-2"
			data-testid={`content-tile-${post.id}`}
			onClick={onClick}
		>
			<div className="relative">
				<BlobImage
					src={slides[0]}
					alt={`Image of Instagram post${dateMoment ? ` scheduled for ${dateMoment.format('dddd MM/DD')}` : ''}`}
					className={cn(
						'aspect-square h-full w-full rounded-lg object-cover',
						'opacity-90 transition-opacity group-hover:opacity-100',
					)}
					fallback={{ isGenerating }}
				/>
				{slides.length > 1 && (
					<div
						className="pointer-events-none absolute top-1.5 right-1.5
							rounded-full bg-black/60 px-1.5 py-0.5 text-[10px] font-medium
							text-white"
					>
						1/{slides.length}
					</div>
				)}
				{isSelectable && onToggleSelect && (
					<TileSelectionCheckbox
						label={post.content.caption?.split('\n')[0]?.slice(0, 60) || 'post'}
						isSelected={isSelected ?? false}
						onToggle={onToggleSelect}
					/>
				)}
			</div>
			<div className="flex flex-col gap-1">
				<div className="flex justify-between">
					<div
						className={cn(
							'line-clamp-1 flex-1',
							style({
								font: 'title',
								fontSize: 'title-sm',
								fontWeight: 'bold',
								color: 'title',
							}),
						)}
					>
						{post.content.caption}
					</div>
					<InstagramLogo className="h-5 w-5" />
				</div>
				<TileFooter
					status={status}
					date={dateMoment}
					publishState={publishState}
				/>
			</div>
		</div>
	);
};
